import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Dropdown, Select, notification } from "antd";
import {
   EditOutlined,
   BankOutlined,
   MailOutlined,
   PhoneOutlined,
   EnvironmentOutlined,
   GlobalOutlined,
   ExportOutlined,
   DownOutlined
} from "@ant-design/icons";
import AuthService from "../../../base/services/authentication.service";
import { httpService } from "../../../base/services/httpService.service";
import { convertTZ } from "../../../base/utils/timezoneConverter";
import { UserProtectionError, UserProtectionSuccess } from "../../../assets/icons";
import RepresentativeDetailsModal from "./components/RepresentativeDetailsModal";
import EditRepresentativeDetailsModal from "./components/EditRepresentativeDetailsModal";
import DeleteRepresentativeModal from "./components/DeleteRepresentativeModal";
import EditProfileModal from "./components/EditProfileModal";
import { useTranslation } from "../../../i18n/LanguageContext";
import "./components/organizationProfileSelf.scss";

const DONUT_TRACK_COLOR = "#DFE9FF";

const toSafeNumber = (value, fallback = 0) => {
   const parsed = Number(value);
   return Number.isFinite(parsed) ? parsed : fallback;
};

const formatScoreValue = (value) => {
   const parsed = toSafeNumber(value);
   return Number.isInteger(parsed) ? parsed : parsed.toFixed(2);
};

// Self-contained copy of ORGorganization.js's donut chart - duplicated
// rather than shared so this new page carries no risk of affecting that
// other, heavily role-branched page.
const AssessmentDonutChart = ({ percent, color }) => {
   const safePercent = Math.max(0, Math.min(100, toSafeNumber(percent)));
   const centerText = `${formatScoreValue(safePercent)}%`;

   const options = useMemo(
      () => ({
         chart: { type: "donut", toolbar: { show: false }, sparkline: { enabled: true } },
         colors: [color, DONUT_TRACK_COLOR],
         dataLabels: { enabled: false },
         fill: { opacity: 1 },
         labels: ["Score", "Remaining"],
         legend: { show: false },
         stroke: { lineCap: "round", width: 0 },
         tooltip: { enabled: false },
         plotOptions: {
            pie: {
               expandOnClick: false,
               donut: {
                  size: "78%",
                  labels: { show: false }
               }
            }
         },
         states: {
            active: { filter: { type: "none" } },
            hover: { filter: { type: "none" } }
         }
      }),
      [color]
   );

   // ApexCharts' own donut.labels.total (SVG-rendered center label) was
   // proving unreliable here - it'd go blank on some option updates while
   // the arc itself stayed correct. A plain HTML overlay is guaranteed to
   // render regardless of ApexCharts' internal re-render quirks.
   return (
      <div className="organization-assessment-donut">
         <Chart options={options} series={[safePercent, 100 - safePercent]} type="donut" height={170} />
         <div className="organization-assessment-donut-center" style={{ color }}>
            {centerText}
         </div>
      </div>
   );
};

const OrganizationProfileSelf = () => {
   const { t } = useTranslation();
   const organizationId = AuthService.getUser()?.organizationId;

   const [organization, setOrganization] = useState(null);
   const [assessmentList, setAssessmentList] = useState([]);
   const [paidAssessmentList, setPaidAssessmentList] = useState([]);
   const [selectedAssessment, setSelectedAssessment] = useState(null);
   const [selectedPaidAssessment, setSelectedPaidAssessment] = useState(null);
   const [percentage, setPercentage] = useState(0);
   const [paidPercentage, setPaidPercentage] = useState(0);
   const [workspaceMenu, setWorkspaceMenu] = useState([]);
   const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
   const [summaryCounts, setSummaryCounts] = useState(null);

   const [showEditProfileModal, setShowEditProfileModal] = useState(false);
   const [showAddRepresentativeModal, setShowAddRepresentativeModal] = useState(false);
   const [showEditRepresentativeModal, setShowEditRepresentativeModal] = useState(false);
   const [showDeleteRepresentativeModal, setShowDeleteRepresentativeModal] = useState(false);
   const [selectedRepresentativeId, setSelectedRepresentativeId] = useState(null);
   const [loadingDeleteRepresentative, setLoadingDeleteRepresentative] = useState(false);

   const refetchOrganization = () => {
      if (!organizationId) return;
      httpService.get(`/Organizations/${organizationId}`, (res) => setOrganization(res.data));
   };

   useEffect(() => {
      refetchOrganization();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [organizationId]);

   // Same counts as the Account summary page, so both pages show identical numbers.
   useEffect(() => {
      httpService
         .get("/DataUsage/ngo/numbers", (res) => setSummaryCounts(res?.data))
         .catch(() => {});
   }, []);

   useEffect(() => {
      if (!organizationId) return;
      setSelectedAssessment(null);
      setSelectedPaidAssessment(null);
      setPercentage(0);
      setPaidPercentage(0);
      const workspaceQuery = selectedWorkspaceId ? `?workspaceId=${selectedWorkspaceId}` : "";
      httpService.get(
         `/Organizations/${organizationId}/self-assessment/score${workspaceQuery}`,
         (res) => {
            setAssessmentList(
               res.data.map((assessment) => ({
                  value: assessment.assessmentId,
                  label: assessment.assessmentName,
                  score: assessment.assessmentScore,
                  maxScore: assessment.assessmentMaxScore
               }))
            );
         }
      );
      httpService.get(
         `/Organizations/${organizationId}/paid-assessment/score${workspaceQuery}`,
         (res) => {
            setPaidAssessmentList(
               res.data.map((assessment) => ({
                  value: assessment.assessmentId,
                  label: assessment.assessmentName,
                  score: assessment.assessmentScore,
                  maxScore: assessment.assessmentMaxScore
               }))
            );
         }
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [organizationId, selectedWorkspaceId]);

   // Populates the "All workspaces" dropdown: every workspace assigned to
   // this org, each with the self + guided assessments assigned inside it.
   useEffect(() => {
      if (!organizationId) return;
      let cancelled = false;

      const fetchWorkspaceAssessments = (workspaceId) =>
         Promise.all([
            new Promise((resolve) => {
               httpService.get(
                  `/Workspaces/${workspaceId}/self-assessments?organizationId=${organizationId}`,
                  (res) => resolve(Array.isArray(res?.data) ? res.data : []),
                  () => resolve([])
               );
            }),
            new Promise((resolve) => {
               httpService.get(
                  `/Workspaces/${workspaceId}/guided-assessments?organizationId=${organizationId}`,
                  (res) => resolve(Array.isArray(res?.data) ? res.data : []),
                  () => resolve([])
               );
            })
         ]);

      const loadWorkspaceMenu = async () => {
         const workspaces = await new Promise((resolve) => {
            httpService.get(
               `/Workspaces/get-my-workspaces?pageSize=100&pageNumber=1`,
               (res) => resolve(Array.isArray(res?.data) ? res.data : []),
               () => resolve([])
            );
         });

         const withAssessments = await Promise.all(
            workspaces.map(async (workspace) => {
               const [selfAssessments, guidedAssessments] = await fetchWorkspaceAssessments(
                  workspace.id
               );
               return {
                  id: workspace.id,
                  name: workspace.name,
                  assessments: [...selfAssessments, ...guidedAssessments]
               };
            })
         );

         if (!cancelled) setWorkspaceMenu(withAssessments);
      };

      loadWorkspaceMenu();
      return () => {
         cancelled = true;
      };
   }, [organizationId]);

   const notifySuccess = (title, description) => {
      notification.open({
         description: (
            <div className="custom-notification-container">
               <div className="custom-notification-image-container">
                  <img src={UserProtectionSuccess} alt="" />
               </div>
               <div className="custom-notification-text-container">
                  <h3>{title}</h3>
                  <p>{description}</p>
               </div>
            </div>
         ),
         placement: "top"
      });
   };

   const notifyError = (title, description) => {
      notification.open({
         description: (
            <div className="custom-notification-container-error">
               <div className="custom-notification-image-container">
                  <img src={UserProtectionError} alt="" />
               </div>
               <div className="custom-notification-text-container-error">
                  <h3>{title}</h3>
                  <p>{description}</p>
               </div>
            </div>
         ),
         placement: "top"
      });
   };

   const handleDeleteRepresentative = async (representativeId) => {
      setLoadingDeleteRepresentative(true);
      try {
         await httpService.delete(`/Organizations/representative/${representativeId}`);
         notifySuccess(
            t("representative-removed-success-title"),
            t("representative-removed-success-description")
         );
         refetchOrganization();
      } catch (error) {
         notifyError(t("toast-oops-title"), t("representative-remove-error-description"));
      } finally {
         setShowDeleteRepresentativeModal(false);
         setLoadingDeleteRepresentative(false);
      }
   };

   const handleAssessmentSelect = (selectedOption) => {
      setSelectedAssessment(selectedOption);
      const item = assessmentList.find((entry) => entry.value === selectedOption);
      setPercentage(item ? (((item.score || 0) / (item.maxScore || 1)) * 100).toFixed(2) : 0);
   };

   const handlePaidAssessmentSelect = (selectedOption) => {
      setSelectedPaidAssessment(selectedOption);
      const item = paidAssessmentList.find((entry) => entry.value === selectedOption);
      setPaidPercentage(item ? (((item.score || 0) / (item.maxScore || 1)) * 100).toFixed(2) : 0);
   };

   // Defaults each card to its own first assessment as soon as its own list
   // loads - self-assessments only ever default from assessmentList, guided
   // ones only ever from paidAssessmentList, so the two never cross over.
   useEffect(() => {
      if (assessmentList.length > 0) {
         handleAssessmentSelect(assessmentList[0].value);
      } else {
         setSelectedAssessment(null);
         setPercentage(0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [assessmentList]);

   useEffect(() => {
      if (paidAssessmentList.length > 0) {
         handlePaidAssessmentSelect(paidAssessmentList[0].value);
      } else {
         setSelectedPaidAssessment(null);
         setPaidPercentage(0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [paidAssessmentList]);

   const selectedWorkspaceName =
      workspaceMenu.find((workspace) => workspace.id === selectedWorkspaceId)?.name ||
      t("all-workspaces-tab");

   if (!organization) return null;

   const representative = organization?.representative?.[0] || null;
   const sectorNames = (organization?.organizationSectors || []).map((sector) => sector.name);
   const countryNames = (organization?.countries || []).map((country) => country.name);
   const foundedYear = organization?.dateOfEstablishment
      ? new Date(convertTZ(organization.dateOfEstablishment)).getFullYear()
      : null;
   const formatCount = (value) => (value === undefined || value === null ? "-" : value);

   return (
      <div className="org-profile-page">
         <EditProfileModal
            visible={showEditProfileModal}
            organization={organization}
            onCancel={() => setShowEditProfileModal(false)}
            onOk={() => {
               setShowEditProfileModal(false);
               refetchOrganization();
            }}
            handleUpdateOrganization={setOrganization}
         />
         <RepresentativeDetailsModal
            visible={showAddRepresentativeModal}
            onCancel={() => setShowAddRepresentativeModal(false)}
            onOk={() => {
               setShowAddRepresentativeModal(false);
               refetchOrganization();
            }}
            id={organizationId}
            fetchData={refetchOrganization}
            updateOrganization={setOrganization}
         />
         <EditRepresentativeDetailsModal
            visible={showEditRepresentativeModal}
            onCancel={() => setShowEditRepresentativeModal(false)}
            onOk={() => {
               setShowEditRepresentativeModal(false);
               refetchOrganization();
            }}
            id={organizationId}
            representativeId={selectedRepresentativeId}
            fetchData={refetchOrganization}
            updateOrganization={setOrganization}
         />
         <DeleteRepresentativeModal
            visible={showDeleteRepresentativeModal}
            onCancel={() => setShowDeleteRepresentativeModal(false)}
            onOk={() => handleDeleteRepresentative(selectedRepresentativeId)}
            loadingDeleteAllRepresentative={loadingDeleteRepresentative}
         />

         {/* Header card */}
         <div className="org-profile-card">
            <div className="org-profile-header">
               <div className="org-profile-header-main">
                  <div className="org-profile-avatar">
                     <BankOutlined />
                  </div>
                  <div className="org-profile-header-text">
                     <h1 className="org-profile-name">
                        {organization?.internationalName || t("no-information-available-text")}
                     </h1>
                     <div className="org-profile-meta">
                        <div
                           className={`org-profile-status-badge${
                              organization?.isPrivate ? " is-private" : ""
                           }`}
                        >
                           <div className="org-profile-status-dot" />
                           <span>
                              {organization?.isPrivate ? t("private-status") : t("public-status")}
                           </span>
                        </div>
                        {(organization?.baseCountry?.name || organization?.city?.name) && (
                           <>
                              <span className="org-profile-meta-text">⋅</span>
                              <span className="org-profile-meta-text">
                                 {[organization?.baseCountry?.name, organization?.city?.name]
                                    .filter(Boolean)
                                    .join(", ")}
                              </span>
                           </>
                        )}
                        {foundedYear && (
                           <>
                              <span className="org-profile-meta-text">⋅</span>
                              <span className="org-profile-meta-text">
                                 {t("founded-label")} {foundedYear}
                              </span>
                           </>
                        )}
                     </div>
                  </div>
               </div>
               <button
                  type="button"
                  className="org-profile-edit-btn"
                  onClick={() => setShowEditProfileModal(true)}
               >
                  <EditOutlined />
                  {t("edit-action")}
               </button>
            </div>
            <div className="org-profile-stats">
               <div className="org-profile-stat">
                  <div className="org-profile-stat-label">{t("menu-item-assessments")}</div>
                  <div className="org-profile-stat-value">
                     {formatCount(summaryCounts?.assessmentsCount)}
                  </div>
               </div>
               <div className="org-profile-stat">
                  <div className="org-profile-stat-label">{t("menu-item-workspaces")}</div>
                  <div className="org-profile-stat-value">
                     {formatCount(summaryCounts?.workspacesCount)}
                  </div>
               </div>
               <div className="org-profile-stat">
                  <div className="org-profile-stat-label">{t("sector-that-works-label")}</div>
                  <div className="org-profile-stat-value">
                     {sectorNames.length} {t("areas-label")}
                  </div>
               </div>
               <div className="org-profile-stat">
                  <div className="org-profile-stat-label">{t("organizations-size-label")}</div>
                  <div className="org-profile-stat-value">
                     {organization?.organizationSize?.range
                        ? `${organization.organizationSize.range} ${t("staff-label")}`
                        : t("no-information-available-text")}
                  </div>
               </div>
            </div>
         </div>

         {/* About + Representative / Contact */}
         <div className="org-profile-middle-row">
            <div className="org-profile-middle-left">
               <div className="org-profile-card">
                  <div className="org-profile-section-header">
                     <div className="org-profile-section-title">{t("about-label")}</div>
                  </div>
                  <div className="org-profile-section-body">
                     <div className="org-profile-description">
                        {organization?.description || t("no-information-available-text")}
                     </div>
                     <div className="org-profile-field-group">
                        <div className="org-profile-field-label">{t("sector-that-works-label")}</div>
                        <div className="org-profile-tags">
                           {sectorNames.length > 0 ? (
                              sectorNames.map((name, index) => (
                                 <div className="org-profile-tag" key={index}>
                                    {name}
                                 </div>
                              ))
                           ) : (
                              <div className="org-profile-description">
                                 {t("no-information-available-text")}
                              </div>
                           )}
                        </div>
                     </div>
                     <div className="org-profile-field-group">
                        <div className="org-profile-field-label">
                           {t("countries-where-operate-label")}
                        </div>
                        <div className="org-profile-tags">
                           {countryNames.length > 0 ? (
                              countryNames.map((name, index) => (
                                 <div className="org-profile-tag" key={index}>
                                    {name}
                                 </div>
                              ))
                           ) : (
                              <div className="org-profile-description">
                                 {t("no-information-available-text")}
                              </div>
                           )}
                        </div>
                     </div>
                  </div>
               </div>

               <div className="org-profile-card">
                  <div className="org-profile-section-header">
                     <div className="org-profile-section-title">
                        {t("representative-details-title")}
                     </div>
                     <button
                        type="button"
                        className="org-profile-edit-link"
                        onClick={() => {
                           if (representative) {
                              setSelectedRepresentativeId(representative.id);
                              setShowEditRepresentativeModal(true);
                           } else {
                              setShowAddRepresentativeModal(true);
                           }
                        }}
                     >
                        {t("edit-representative-details-button")}
                     </button>
                  </div>
                  <div className="org-profile-section-body">
                     <div className="org-profile-field-pair">
                        <div>
                           <div className="org-profile-field-label">{t("name")}</div>
                           <div className="org-profile-field-value">
                              {representative?.name || t("no-information-available-text")}
                           </div>
                        </div>
                        <div>
                           <div className="org-profile-field-label">{t("email")}</div>
                           <div className="org-profile-field-value">
                              {representative?.email || t("no-information-available-text")}
                           </div>
                        </div>
                     </div>
                     <div className="org-profile-field-pair">
                        <div>
                           <div className="org-profile-field-label">{t("phone-number")}</div>
                           <div className="org-profile-field-value">
                              {representative?.phone || t("no-information-available-text")}
                           </div>
                        </div>
                        <div>
                           <div className="org-profile-field-label">{t("location-label")}</div>
                           <div className="org-profile-field-value">
                              {representative?.location || t("no-information-available-text")}
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <div className="org-profile-middle-right org-profile-card">
               <div className="org-profile-section-header">
                  <div className="org-profile-section-title">{t("contact-label")}</div>
               </div>
               <div className="org-profile-section-body">
                  <div className="org-profile-contact-row">
                     <div className="org-profile-contact-icon">
                        <MailOutlined />
                     </div>
                     <div className="org-profile-contact-text">
                        <div className="org-profile-field-label">{t("email")}</div>
                        <div className="org-profile-field-value">
                           {organization?.email || t("no-information-available-text")}
                        </div>
                     </div>
                  </div>
                  <div className="org-profile-contact-row">
                     <div className="org-profile-contact-icon">
                        <PhoneOutlined />
                     </div>
                     <div className="org-profile-contact-text">
                        <div className="org-profile-field-label">{t("phone-number")}</div>
                        <div className="org-profile-field-value">
                           {organization?.phoneNumber || t("no-information-available-text")}
                        </div>
                     </div>
                  </div>
                  <div className="org-profile-contact-row">
                     <div className="org-profile-contact-icon">
                        <EnvironmentOutlined />
                     </div>
                     <div className="org-profile-contact-text">
                        <div className="org-profile-field-label">{t("address-label")}</div>
                        <div className="org-profile-field-value">
                           {organization?.address || t("no-information-available-text")}
                        </div>
                     </div>
                  </div>
                  <div className="org-profile-contact-row">
                     <div className="org-profile-contact-icon">
                        <EnvironmentOutlined />
                     </div>
                     <div className="org-profile-contact-text">
                        <div className="org-profile-field-label">{t("zip-code-label")}</div>
                        <div className="org-profile-field-value">
                           {organization?.zipCode || t("no-information-available-text")}
                        </div>
                     </div>
                  </div>
                  {console.log("org", organization)}
                  {(organization?.website || organization?.facebook) && (
                     <div className="org-profile-contact-row">
                        <div className="org-profile-contact-icon">
                           <GlobalOutlined />
                        </div>
                        <div className="org-profile-contact-text">
                           <div className="org-profile-field-label">{t("online-label")}</div>
                           {organization?.website && (
                              <a
                                 className="org-profile-online-link"
                                 href={organization.website}
                                 target="_blank"
                                 rel="noopener noreferrer"
                              >
                                 {organization.website} <ExportOutlined />
                              </a>
                           )}
                           {organization?.facebook && (
                              <a
                                 className="org-profile-online-link"
                                 href={organization.facebook}
                                 target="_blank"
                                 rel="noopener noreferrer"
                              >
                                 {organization.facebook} <ExportOutlined />
                              </a>
                           )}
                        </div>
                     </div>
                  )}
               </div>
            </div>
         </div>

         {/* Assessment results */}
         <div className="org-profile-card">
            <div className="org-profile-results-header">
               <div style={{ width: "100%" }}>
                  <h2 className="org-profile-results-title">{t("assessments-scores-title")}</h2>
                  <p className="org-profile-results-subtitle">
                     {t("assessments-scores-description")}
                  </p>
               </div>
               <Dropdown
                  trigger={["click"]}
                  placement="bottomRight"
                  dropdownRender={() => (
                     <div className="org-profile-workspace-menu">
                        <div
                           className={`org-profile-workspace-menu-item${
                              !selectedWorkspaceId ? " active" : ""
                           }`}
                           onClick={() => setSelectedWorkspaceId(null)}
                        >
                           {t("all-workspaces-tab")}
                        </div>
                        {workspaceMenu.map((workspace) => (
                           <div className="org-profile-workspace-menu-group" key={workspace.id}>
                              <div
                                 className={`org-profile-workspace-menu-group-label${
                                    selectedWorkspaceId === workspace.id ? " active" : ""
                                 }`}
                                 onClick={() => setSelectedWorkspaceId(workspace.id)}
                              >
                                 {workspace.name}
                              </div>
                              {workspace.assessments.length > 0 ? (
                                 workspace.assessments.map((assessment) => (
                                    <div
                                       key={assessment.id}
                                       className="org-profile-workspace-menu-assessment"
                                       onClick={() => setSelectedWorkspaceId(workspace.id)}
                                    >
                                       {assessment.name}
                                    </div>
                                 ))
                              ) : (
                                 <div className="org-profile-workspace-menu-assessment empty">
                                    {t("no-assessments-listed-text")}
                                 </div>
                              )}
                           </div>
                        ))}
                     </div>
                  )}
               >
                  <div className="org-profile-workspace-filter" role="button">
                     <span className="org-profile-workspace-filter-label">
                        {selectedWorkspaceName}
                     </span>
                     <DownOutlined />
                  </div>
               </Dropdown>
            </div>
            <div className="org-profile-results-body">
               <div className="org-profile-result-card self">
                  <div className="org-profile-result-title">{t("self-assessment")}</div>
                  {assessmentList.length > 0 ? (
                     <>
                        <Select
                           className="org-profile-result-select"
                           value={selectedAssessment}
                           onChange={handleAssessmentSelect}
                           options={assessmentList}
                           placeholder={t("select-self-assessment-placeholder")}
                        />
                        <AssessmentDonutChart color="#20BAB7" percent={percentage} />
                     </>
                  ) : (
                     <div className="org-profile-result-empty">
                        {t("no-self-assessment-yet-title")}
                     </div>
                  )}
               </div>
               <div className="org-profile-result-card guided">
                  <div className="org-profile-result-title">{t("guided-assessment")}</div>
                  {paidAssessmentList.length > 0 ? (
                     <>
                        <Select
                           className="org-profile-result-select"
                           value={selectedPaidAssessment}
                           onChange={handlePaidAssessmentSelect}
                           options={paidAssessmentList}
                           placeholder={t("select-guided-assessment-placeholder")}
                        />
                        <AssessmentDonutChart color="#5492FF" percent={paidPercentage} />
                     </>
                  ) : (
                     <div className="org-profile-result-empty">
                        {t("no-guided-assessment-yet-title")}
                     </div>
                  )}
               </div>
            </div>
         </div>
      </div>
   );
};

export default OrganizationProfileSelf;
